'use client';

import React, { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase/client';
import { useActiveTenant } from '@/hooks/useActiveTenant';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Eye, User, Building2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useTranslations } from 'next-intl';

interface Tenant {
  id: string;
  name: string;
}

interface Employee {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  tenant_id: string;
}

interface AdminViewAsDropdownProps {
  onViewAsChange?: (tenantId: string | null, employeeId: string | null) => void;
}

/**
 * Admin "View As" Dropdown Component - Fortune 500 Standard
 *
 * Allows admins to impersonate tenants or employees to see exactly what they see.
 * This is a critical admin tool for debugging user issues and providing support.
 */
export function AdminViewAsDropdown({ onViewAsChange }: AdminViewAsDropdownProps) {
  const t = useTranslations('invitation.adminViewAs'); // ✅ FIX: Correct translation path
  // Platform-admin status + loading come from the canonical resolver — no
  // extra getUser() + is_super_admin RPC + tenant_user_roles query per mount.
  // (This component is mounted for ALL admins, so those 3 calls fired on every
  // single admin page-load before.)
  const { isPlatformAdmin, loading: activeTenantLoading } = useActiveTenant();
  const isAdmin = isPlatformAdmin;
  const loading = activeTenantLoading;
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [selectedTenant, setSelectedTenant] = useState<string | null>(null);
  const [selectedEmployee, setSelectedEmployee] = useState<string | null>(null);

  // Fetch all tenants (for admins only)
  useEffect(() => {
    if (!isAdmin) return;

    const fetchTenants = async () => {
      const { data, error } = await supabase
        .from('tenants')
        .select('id, name')
        .order('name');

      if (!error && data) {
        setTenants(data);
      }
    };

    fetchTenants();
  }, [isAdmin]);

  // Fetch employees for selected tenant
  useEffect(() => {
    if (!selectedTenant) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional one-shot mount sync / external store subscription
      setEmployees([]);
      return;
    }

    const fetchEmployees = async () => {
      const { data, error } = await supabase
        .from('employee_profiles')
        .select('id, first_name, last_name, email, tenant_id')
        .eq('tenant_id', selectedTenant)
        .eq('is_active', true)
        .is('deleted_at', null)
        .order('first_name');

      if (!error && data) {
        setEmployees(data);
      }
    };

    fetchEmployees();
  }, [selectedTenant]);

  // Handle tenant change — uses secure API to set `tenant_id` cookie
  // so middleware + RLS resolve to the chosen tenant.
  const handleTenantChange = async (tenantId: string) => {
    if (tenantId === 'none') {
      setSelectedTenant(null);
      setSelectedEmployee(null);
      localStorage.removeItem('adminViewAsTenant');
      localStorage.removeItem('adminViewAsEmployee');
      // Clear cookie via API (passing empty tenantId won't work; just unset locally)
      document.cookie = 'tenant_id=; path=/; max-age=0';
      onViewAsChange?.(null, null);
      window.location.reload();
      return;
    }

    try {
      const res = await fetch('/api/admin/switch-tenant', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tenantId }),
      });
      if (!res.ok) {
        // eslint-disable-next-line no-console
        console.error('[AdminViewAs] switch failed', await res.text());
        return;
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[AdminViewAs] switch error', err);
      return;
    }

    setSelectedTenant(tenantId);
    setSelectedEmployee(null);
    localStorage.setItem('adminViewAsTenant', tenantId);
    localStorage.removeItem('adminViewAsEmployee');
    onViewAsChange?.(tenantId, null);
    window.location.reload();
  };

  // Handle employee change
  const handleEmployeeChange = (employeeId: string) => {
    if (employeeId === 'owner') {
      // View as tenant owner
      setSelectedEmployee(null);
      localStorage.removeItem('adminViewAsEmployee');
      onViewAsChange?.(selectedTenant, null);
      window.location.reload();
      return;
    }

    setSelectedEmployee(employeeId);
    localStorage.setItem('adminViewAsEmployee', employeeId);
    onViewAsChange?.(selectedTenant, employeeId);
    window.location.reload(); // Reload to apply employee view
  };

  // Clear view-as mode
  const clearViewAs = () => {
    setSelectedTenant(null);
    setSelectedEmployee(null);
    localStorage.removeItem('adminViewAsTenant');
    localStorage.removeItem('adminViewAsEmployee');
    onViewAsChange?.(null, null);
    window.location.reload();
  };

  // Load saved view-as state on mount
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const savedTenant = localStorage.getItem('adminViewAsTenant');
      const savedEmployee = localStorage.getItem('adminViewAsEmployee');

      if (savedTenant) {
        // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional one-shot mount sync / external store subscription
        setSelectedTenant(savedTenant);
      }

      if (savedEmployee) {
        setSelectedEmployee(savedEmployee);
      }
    }
  }, []);

  // Don't show for non-admins
  if (!isAdmin || loading) {
    return null;
  }

  return (
    <div className="flex flex-wrap items-center justify-end gap-2 border-b border-white/40 bg-white/70 px-4 py-2 backdrop-blur-sm sm:gap-4 sm:px-6 sm:py-3">
      {selectedTenant && (
        <Badge variant="secondary" className="flex items-center gap-1 bg-blue-100 text-blue-700">
          <Eye className="h-3 w-3" />
          {t('adminViewActive')}
        </Badge>
      )}

      <Select
        value={selectedTenant || 'none'}
        onValueChange={handleTenantChange}
      >
        <SelectTrigger className="w-full min-w-[160px] sm:w-[240px]">
          <div className="flex items-center gap-2">
            <Building2 className="h-4 w-4" />
            <SelectValue placeholder={t('selectTenant')} />
          </div>
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="none">
            <div className="flex items-center gap-2">
              <User className="h-4 w-4" />
              {t('viewAsAdmin')}
            </div>
          </SelectItem>
          {tenants.map((tenant) => (
            <SelectItem key={tenant.id} value={tenant.id}>
              {tenant.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {selectedTenant && (
        <Select
          value={selectedEmployee || 'owner'}
          onValueChange={handleEmployeeChange}
        >
          <SelectTrigger className="w-full min-w-[140px] sm:w-[200px]">
            <div className="flex items-center gap-2">
              <User className="h-4 w-4" />
              <SelectValue placeholder={t('selectEmployee')} />
            </div>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="owner">
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                {t('businessOwner')}
              </div>
            </SelectItem>
            {employees.map((employee) => (
              <SelectItem key={employee.id} value={employee.id}>
                {employee.first_name} {employee.last_name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {selectedTenant && (
        <Button
          variant="ghost"
          size="sm"
          onClick={clearViewAs}
          className="h-9 px-2"
        >
          <X className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}
